import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Pencil, Trash2, Upload, Save, X, Loader2, ChevronUp, ChevronDown, Mail, CalendarDays, Inbox as InboxIcon, Ban, Circle, CheckCircle2 } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuth";
import { useSiteData } from "@/context/SiteData";
import {
  fetchArtworks, fetchPricing, adminUpload,
  adminCreateArtwork, adminUpdateArtwork, adminDeleteArtwork,
  adminUpdateContent, adminUpdatePricing, adminCreatePricing, adminDeletePricing,
  adminListTestimonials, adminCreateTestimonial, adminUpdateTestimonial, adminDeleteTestimonial,
  adminGetAvailability, adminSetRecurring, adminSetOverride, adminDeleteOverride,
  adminInboxBookings, adminInboxMessages, adminReadBooking, adminDeleteBooking, adminReadMessage, adminDeleteMessage,
  apiErr, formatPrice,
} from "@/lib/api";

const TABS = ["Artworks", "Availability", "Inbox", "Site Text", "Pricing", "Testimonials"];
const field = "field mt-2";
const label = "kicker";

function Panel({ children }) {
  return <div className="rounded-sm border border-[#D4AF37]/30 bg-white/70 p-6 sm:p-8">{children}</div>;
}

/* ---------------- Artworks ---------------- */
function ArtworkForm({ initial, onClose, onSaved }) {
  const [f, setF] = useState(
    initial || { title: "", price: "", medium: "", size: "", year: "", description: "", category: "Original Artwork", featured: false, images: [], order: 0 }
  );
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const { url } = await adminUpload(file);
      setF((p) => ({ ...p, images: [...(p.images || []), url] }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(apiErr(err, "Upload failed"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const moveImg = (idx, dir) =>
    setF((p) => {
      const arr = [...(p.images || [])];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return p;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...p, images: arr };
    });
  const removeImg = (idx) => setF((p) => ({ ...p, images: (p.images || []).filter((_, i) => i !== idx) }));

  const save = async () => {
    if (!f.title.trim()) return toast.error("Title is required");
    setBusy(true);
    const payload = {
      ...f,
      price: f.price === "" || f.price === null ? null : Number(f.price),
      order: Number(f.order) || 0,
    };
    try {
      if (initial?.id) await adminUpdateArtwork(initial.id, payload);
      else await adminCreateArtwork(payload);
      toast.success("Saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/50 p-4" data-testid="artwork-form-modal">
      <div className="my-8 w-full max-w-2xl rounded-sm border border-[#D4AF37]/40 bg-[#FAF7F2] p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <h3 className="font-serif text-2xl text-[#1C1815]">{initial?.id ? "Edit Artwork" : "Add Artwork"}</h3>
          <button onClick={onClose} data-testid="artwork-form-close"><X className="h-5 w-5 text-[#8C7E70]" /></button>
        </div>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <label className="block sm:col-span-2"><span className={label}>Title</span>
            <input className={field} value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="artwork-title-input" /></label>
          <label className="block"><span className={label}>Price (USD, blank = Inquire)</span>
            <input type="number" className={field} value={f.price ?? ""} onChange={(e) => set("price", e.target.value)} data-testid="artwork-price-input" /></label>
          <label className="block"><span className={label}>Category</span>
            <input className={field} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="artwork-category-input" /></label>
          <label className="block"><span className={label}>Medium</span>
            <input className={field} value={f.medium} onChange={(e) => set("medium", e.target.value)} data-testid="artwork-medium-input" /></label>
          <label className="block"><span className={label}>Size</span>
            <input className={field} value={f.size} onChange={(e) => set("size", e.target.value)} data-testid="artwork-size-input" /></label>
          <label className="block"><span className={label}>Year</span>
            <input className={field} value={f.year} onChange={(e) => set("year", e.target.value)} data-testid="artwork-year-input" /></label>
          <label className="block"><span className={label}>Display order</span>
            <input type="number" className={field} value={f.order} onChange={(e) => set("order", e.target.value)} data-testid="artwork-order-input" /></label>
          <label className="block sm:col-span-2"><span className={label}>Description (max 2000 chars)</span>
            <textarea rows={4} maxLength={2000} className={`${field} resize-none`} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="artwork-description-input" /></label>
          <div className="sm:col-span-2">
            <span className={label}>Images (first = main; use arrows to reorder)</span>
            <div className="mt-3 flex flex-wrap gap-3" data-testid="artwork-images-list">
              {(f.images || []).map((src, idx) => (
                <div key={src + idx} className="w-24">
                  <div className="relative">
                    <img src={src} alt="" className="h-24 w-24 rounded-sm border border-[#D4AF37]/40 object-cover" data-testid={`artwork-image-thumb-${idx}`} />
                    {idx === 0 && (
                      <span className="absolute left-1 top-1 rounded-full bg-[#D4AF37]/90 px-2 py-0.5 font-mono text-[8px] uppercase text-[#1C1815]">Main</span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center justify-center gap-1">
                    <button type="button" onClick={() => moveImg(idx, -1)} disabled={idx === 0} className="rounded p-1 text-[#4A423A] hover:bg-[#D4AF37]/10 disabled:opacity-30" data-testid={`artwork-image-up-${idx}`}><ChevronUp className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => moveImg(idx, 1)} disabled={idx === (f.images.length - 1)} className="rounded p-1 text-[#4A423A] hover:bg-[#D4AF37]/10 disabled:opacity-30" data-testid={`artwork-image-down-${idx}`}><ChevronDown className="h-3.5 w-3.5" /></button>
                    <button type="button" onClick={() => removeImg(idx)} className="rounded p-1 text-red-600 hover:bg-red-50" data-testid={`artwork-image-remove-${idx}`}><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </div>
              ))}
              <div className="flex h-24 w-24 items-center justify-center rounded-sm border border-dashed border-[#D4AF37]/40">
                <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid="artwork-image-input" />
                <button type="button" onClick={() => fileRef.current?.click()} className="flex flex-col items-center gap-1 text-[10px] uppercase text-[#8C7E70]" data-testid="artwork-upload-button">
                  {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Upload className="h-5 w-5" />}
                  {uploading ? "…" : "Add"}
                </button>
              </div>
            </div>
          </div>
          <label className="flex items-center gap-3 sm:col-span-2">
            <input type="checkbox" checked={!!f.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="artwork-featured-input" />
            <span className="text-sm text-[#3D352E]">Featured (shown on homepage)</span>
          </label>
        </div>
        <div className="mt-8 flex justify-end gap-3">
          <button onClick={onClose} className="btn-ghost-light !border-[#1C1815]/20 !text-[#4A423a]">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary disabled:opacity-60" data-testid="artwork-save-button">
            <Save className="h-4 w-4" /> {busy ? "Saving…" : "Save Artwork"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ArtworksPanel() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const { refetch } = useSiteData();

  const load = () => fetchArtworks().then(setList).catch(() => {});
  useEffect(() => { load(); }, []);
  const afterSave = () => { load(); refetch?.(); };

  const del = async (id) => {
    if (!window.confirm("Delete this artwork? It will be removed from the public gallery.")) return;
    try { await adminDeleteArtwork(id); toast.success("Deleted"); afterSave(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Panel>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl text-[#1C1815]">Artwork ({list.length})</h2>
        <button className="btn-primary" onClick={() => { setEditing(null); setOpen(true); }} data-testid="add-artwork-button">
          <Plus className="h-4 w-4" /> Add Artwork
        </button>
      </div>
      <div className="mt-6 divide-y divide-[#D4AF37]/20">
        {list.map((a) => (
          <div key={a.id} className="flex items-center gap-4 py-4" data-testid={`admin-artwork-row-${a.id}`}>
            {a.images?.[0]
              ? <img src={a.images[0]} alt={a.title} className="h-14 w-14 rounded-sm border border-[#D4AF37]/30 object-cover" />
              : <div className="h-14 w-14 rounded-sm border border-dashed border-[#D4AF37]/30" />}
            <div className="min-w-0 flex-1">
              <p className="truncate font-serif text-lg text-[#1C1815]">{a.title} {a.featured && <span className="ml-2 rounded-full bg-[#D4AF37]/20 px-2 py-0.5 font-mono text-[9px] uppercase text-[#9A7A3F]">Featured</span>}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C7E70]">{a.category} · {formatPrice(a.price)}</p>
            </div>
            <button className="rounded-sm p-2 text-[#4A423A] hover:bg-[#D4AF37]/10" onClick={() => { setEditing(a); setOpen(true); }} data-testid={`edit-artwork-${a.id}`}><Pencil className="h-4 w-4" /></button>
            <button className="rounded-sm p-2 text-red-600 hover:bg-red-50" onClick={() => del(a.id)} data-testid={`delete-artwork-${a.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        ))}
        {list.length === 0 && <p className="py-8 text-center text-sm text-[#8C7E70]">No artwork yet. Add your first piece.</p>}
      </div>
      {open && <ArtworkForm initial={editing} onClose={() => setOpen(false)} onSaved={afterSave} />}
    </Panel>
  );
}

/* ---------------- Site Text ---------------- */
const TEXT_FIELDS = [
  { key: "hero_line1", label: "Hero line 1", type: "input" },
  { key: "hero_line2", label: "Hero line 2", type: "input" },
  { key: "hero_line3", label: "Hero line 3 (accent)", type: "input" },
  { key: "hero_intro", label: "Hero intro paragraph", type: "area", max: 400 },
  { key: "about_bio", label: "About / Bio (blank line separates paragraphs)", type: "area", max: 2500 },
  { key: "mission_statement", label: "Mission statement", type: "area", max: 600 },
  { key: "service_desc_1", label: "Service 1 — Original Artwork", type: "area", max: 600 },
  { key: "service_desc_2", label: "Service 2 — Custom / Commissioned", type: "area", max: 600 },
  { key: "service_desc_3", label: "Service 3 — Consultations", type: "area", max: 600 },
  { key: "service_desc_4", label: "Service 4 — Prints & Products", type: "area", max: 600 },
  { key: "contact_email", label: "Contact email", type: "input" },
  { key: "contact_phone", label: "Contact phone", type: "input" },
  { key: "contact_instagram", label: "Instagram handle", type: "input" },
  { key: "policy_shipping", label: "Shipping policy", type: "area", max: 1200 },
  { key: "policy_returns", label: "Returns policy", type: "area", max: 1200 },
  { key: "policy_refund", label: "Refund policy", type: "area", max: 1200 },
  { key: "footer_tagline", label: "Footer tagline", type: "input" },
];

function SiteTextPanel() {
  const { content, refetch } = useSiteData();
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setForm(content || {}); }, [content]);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      const payload = {};
      TEXT_FIELDS.forEach((f) => { payload[f.key] = form[f.key] ?? ""; });
      await adminUpdateContent(payload);
      await refetch?.();
      toast.success("Site text updated");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <Panel>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl text-[#1C1815]">Editable Site Text</h2>
        <button className="btn-primary" onClick={save} disabled={busy} data-testid="save-content-button"><Save className="h-4 w-4" /> {busy ? "Saving…" : "Save All"}</button>
      </div>
      <div className="mt-6 grid gap-5">
        {TEXT_FIELDS.map((f) => (
          <label key={f.key} className="block" data-testid={`content-field-${f.key}`}>
            <span className={label}>{f.label}</span>
            {f.type === "area"
              ? <textarea rows={4} maxLength={f.max} className={`${field} resize-none`} value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} />
              : <input className={field} value={form[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} />}
          </label>
        ))}
      </div>
    </Panel>
  );
}

/* ---------------- Pricing ---------------- */
function PricingPanel() {
  const [list, setList] = useState([]);
  const { refetch } = useSiteData();
  const load = () => fetchPricing().then(setList).catch(() => {});
  useEffect(() => { load(); }, []);

  const update = (idx, k, v) => setList((p) => p.map((t, i) => (i === idx ? { ...t, [k]: v } : t)));

  const save = async (tier) => {
    try {
      const payload = { ...tier, price: tier.price === "" || tier.price === null ? null : Number(tier.price), order: Number(tier.order) || 0,
        features: Array.isArray(tier.features) ? tier.features : String(tier.features || "").split(",").map((s) => s.trim()).filter(Boolean) };
      await adminUpdatePricing(tier.id, payload);
      await refetch?.();
      toast.success(`Saved "${tier.title}"`);
    } catch (e) { toast.error(apiErr(e)); }
  };

  const add = async () => {
    try { await adminCreatePricing({ title: "New Tier", price: 0, price_label: "", unit: "", description: "", features: [], buyable: false, order: list.length }); load(); refetch?.(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this pricing tier?")) return;
    try { await adminDeletePricing(id); load(); refetch?.(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Panel>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl text-[#1C1815]">Pricing Tiers</h2>
        <button className="btn-primary" onClick={add} data-testid="add-pricing-button"><Plus className="h-4 w-4" /> Add Tier</button>
      </div>
      <p className="mt-2 text-sm font-light text-[#8C7E70]">Tiers marked “Buyable” show a Buy button and charge the price below via Stripe.</p>
      <div className="mt-6 space-y-6">
        {list.map((t, idx) => (
          <div key={t.id} className="rounded-sm border border-[#D4AF37]/25 bg-[#F4EFE6]/50 p-5" data-testid={`admin-pricing-row-${t.id}`}>
            <div className="grid gap-4 sm:grid-cols-3">
              <label className="block"><span className={label}>Title</span><input className={field} value={t.title} onChange={(e) => update(idx, "title", e.target.value)} data-testid={`pricing-title-${t.id}`} /></label>
              <label className="block"><span className={label}>Price (USD)</span><input type="number" className={field} value={t.price ?? ""} onChange={(e) => update(idx, "price", e.target.value)} data-testid={`pricing-price-${t.id}`} /></label>
              <label className="block"><span className={label}>Price label (e.g. Starting at $20)</span><input className={field} value={t.price_label ?? ""} onChange={(e) => update(idx, "price_label", e.target.value)} /></label>
              <label className="block"><span className={label}>Unit</span><input className={field} value={t.unit ?? ""} onChange={(e) => update(idx, "unit", e.target.value)} /></label>
              <label className="block sm:col-span-2"><span className={label}>Description</span><input className={field} value={t.description ?? ""} onChange={(e) => update(idx, "description", e.target.value)} /></label>
              <label className="block sm:col-span-3"><span className={label}>Features (comma separated)</span><input className={field} value={Array.isArray(t.features) ? t.features.join(", ") : t.features} onChange={(e) => update(idx, "features", e.target.value)} /></label>
              <label className="flex items-center gap-3"><input type="checkbox" checked={!!t.buyable} onChange={(e) => update(idx, "buyable", e.target.checked)} data-testid={`pricing-buyable-${t.id}`} /><span className="text-sm text-[#3D352E]">Buyable (Stripe)</span></label>
            </div>
            <div className="mt-4 flex justify-end gap-3">
              <button className="btn-ghost-light !border-red-200 !text-red-600" onClick={() => del(t.id)}><Trash2 className="h-4 w-4" /> Delete</button>
              <button className="btn-primary" onClick={() => save(t)} data-testid={`save-pricing-${t.id}`}><Save className="h-4 w-4" /> Save</button>
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
}

/* ---------------- Testimonials ---------------- */
function TestimonialsPanel() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", quote: "", rating: 5, status: "approved" });
  const load = () => adminListTestimonials().then(setList).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (form.name.trim().length < 1 || form.quote.trim().length < 3) return toast.error("Name and quote are required");
    try { await adminCreateTestimonial(form); setForm({ name: "", location: "", quote: "", rating: 5, status: "approved" }); load(); toast.success("Added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const setStatus = async (t, status) => {
    try { await adminUpdateTestimonial(t.token, { name: t.name, location: t.location || "", quote: t.quote, rating: t.rating || 5, status }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (token) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try { await adminDeleteTestimonial(token); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <Panel>
      <h2 className="font-serif text-xl text-[#1C1815]">Testimonials</h2>
      <div className="mt-6 rounded-sm border border-[#D4AF37]/25 bg-[#F4EFE6]/50 p-5">
        <p className="kicker">Add a testimonial</p>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <input className={field} placeholder="Name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} data-testid="testi-name-input" />
          <input className={field} placeholder="Location (optional)" value={form.location} onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))} />
          <textarea rows={3} maxLength={800} className={`${field} resize-none sm:col-span-2`} placeholder="Quote" value={form.quote} onChange={(e) => setForm((f) => ({ ...f, quote: e.target.value }))} data-testid="testi-quote-input" />
          <label className="flex items-center gap-2"><span className={label}>Rating</span>
            <select className="field mt-2 w-24" value={form.rating} onChange={(e) => setForm((f) => ({ ...f, rating: Number(e.target.value) }))}>
              {[5, 4, 3, 2, 1].map((n) => <option key={n} value={n}>{n} ★</option>)}
            </select>
          </label>
        </div>
        <div className="mt-4 flex justify-end"><button className="btn-primary" onClick={add} data-testid="add-testimonial-button"><Plus className="h-4 w-4" /> Add</button></div>
      </div>
      <div className="mt-6 divide-y divide-[#D4AF37]/20">
        {list.map((t) => (
          <div key={t.token} className="py-4" data-testid={`admin-testi-row-${t.token}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="font-serif text-lg text-[#1C1815]">{t.name} <span className="ml-1 text-[#B89759]">{"★".repeat(t.rating || 0)}</span></p>
                <p className="mt-1 text-sm font-light text-[#4A423A]">“{t.quote}”</p>
                <span className={`mt-2 inline-block rounded-full px-2 py-0.5 font-mono text-[9px] uppercase ${t.status === "approved" ? "bg-green-100 text-green-700" : "bg-[#D4AF37]/20 text-[#9A7A3F]"}`}>{t.status}</span>
              </div>
              <div className="flex shrink-0 gap-2">
                {t.status !== "approved"
                  ? <button className="rounded-sm px-3 py-1 text-xs text-green-700 hover:bg-green-50" onClick={() => setStatus(t, "approved")} data-testid={`approve-testi-${t.token}`}>Approve</button>
                  : <button className="rounded-sm px-3 py-1 text-xs text-[#8C7E70] hover:bg-[#D4AF37]/10" onClick={() => setStatus(t, "pending")} data-testid={`unpublish-testi-${t.token}`}>Unpublish</button>}
                <button className="rounded-sm p-2 text-red-600 hover:bg-red-50" onClick={() => del(t.token)} data-testid={`delete-testi-${t.token}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {list.length === 0 && <p className="py-8 text-center text-sm text-[#8C7E70]">No testimonials yet.</p>}
      </div>
    </Panel>
  );
}

const WEEKDAYS = [["Mon", 0], ["Tue", 1], ["Wed", 2], ["Thu", 3], ["Fri", 4], ["Sat", 5], ["Sun", 6]];

function AvailabilityPanel() {
  const [slots, setSlots] = useState([]);
  const [recur, setRecur] = useState({});
  const [overrides, setOverrides] = useState([]);
  const [ovDate, setOvDate] = useState("");
  const [ovStatus, setOvStatus] = useState("blocked");
  const [ovTimes, setOvTimes] = useState([]);

  const load = () =>
    adminGetAvailability().then((d) => {
      setSlots(d.slots || []);
      const map = {};
      (d.recurring || []).forEach((r) => { map[r.weekday] = r.times; });
      setRecur(map);
      setOverrides(d.overrides || []);
    }).catch(() => {});
  useEffect(() => { load(); }, []);

  const toggleRecur = (wd, t) =>
    setRecur((p) => {
      const cur = new Set(p[wd] || []);
      cur.has(t) ? cur.delete(t) : cur.add(t);
      return { ...p, [wd]: [...cur] };
    });
  const saveRecur = async () => {
    const rules = Object.entries(recur).map(([wd, times]) => ({ weekday: Number(wd), times })).filter((r) => r.times.length);
    try { await adminSetRecurring(rules); toast.success("Weekly availability saved"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleOvTime = (t) => setOvTimes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));
  const addOverride = async () => {
    if (!ovDate) return toast.error("Pick a date");
    try {
      await adminSetOverride({ date: ovDate, status: ovStatus, times: ovStatus === "open" ? ovTimes : [] });
      toast.success("Date override saved");
      setOvDate(""); setOvTimes([]); setOvStatus("blocked"); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const delOverride = async (id) => { try { await adminDeleteOverride(id); load(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="space-y-6">
      <Panel>
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-xl text-[#1C1815]">Weekly Availability</h2>
          <button className="btn-primary" onClick={saveRecur} data-testid="save-recurring-button"><Save className="h-4 w-4" /> Save Weekly</button>
        </div>
        <p className="mt-2 text-sm font-light text-[#8C7E70]">Tap the times you're open each weekday. These repeat every week.</p>
        <div className="mt-5 space-y-3">
          {WEEKDAYS.map(([lbl, wd]) => (
            <div key={wd} className="flex flex-wrap items-center gap-2" data-testid={`recur-day-${wd}`}>
              <span className="w-12 font-mono text-[11px] uppercase tracking-[0.15em] text-[#4A423A]">{lbl}</span>
              {slots.map((t) => {
                const on = (recur[wd] || []).includes(t);
                return (
                  <button key={t} type="button" onClick={() => toggleRecur(wd, t)}
                    className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${on ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]" : "border-[#D4AF37]/40 text-[#4A423A] hover:border-[#B89759]"}`}
                    data-testid={`recur-slot-${wd}-${t.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    {t}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </Panel>

      <Panel>
        <h2 className="font-serif text-xl text-[#1C1815]">Specific Dates</h2>
        <p className="mt-2 text-sm font-light text-[#8C7E70]">Block a day off (vacation, already booked) or open extra times on a one-off date. These override the weekly schedule.</p>
        <div className="mt-5 grid gap-4 sm:grid-cols-3">
          <label className="block"><span className={label}>Date</span>
            <input type="date" min={new Date().toISOString().slice(0, 10)} className={field} value={ovDate} onChange={(e) => setOvDate(e.target.value)} data-testid="override-date-input" /></label>
          <label className="block"><span className={label}>Action</span>
            <select className="field mt-2" value={ovStatus} onChange={(e) => setOvStatus(e.target.value)} data-testid="override-status-select">
              <option value="blocked">Block whole day</option>
              <option value="open">Open specific times</option>
            </select></label>
          <div className="flex items-end"><button className="btn-primary w-full" onClick={addOverride} data-testid="add-override-button"><Plus className="h-4 w-4" /> Save Date</button></div>
        </div>
        {ovStatus === "open" && (
          <div className="mt-4 flex flex-wrap gap-2">
            {slots.map((t) => (
              <button key={t} type="button" onClick={() => toggleOvTime(t)}
                className={`rounded-full border px-3 py-1.5 text-xs transition-colors ${ovTimes.includes(t) ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]" : "border-[#D4AF37]/40 text-[#4A423A] hover:border-[#B89759]"}`}>
                {t}
              </button>
            ))}
          </div>
        )}
        <div className="mt-6 divide-y divide-[#D4AF37]/20">
          {overrides.map((o) => (
            <div key={o.id} className="flex items-center justify-between py-3" data-testid={`override-row-${o.date}`}>
              <div className="flex items-center gap-3">
                {o.status === "blocked" ? <Ban className="h-4 w-4 text-red-500" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />}
                <span className="text-sm text-[#1C1815]">{o.date}</span>
                <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C7E70]">
                  {o.status === "blocked" ? "Blocked" : `Open · ${(o.times || []).length} times`}
                </span>
              </div>
              <button className="rounded-sm p-2 text-red-600 hover:bg-red-50" onClick={() => delOverride(o.id)} data-testid={`delete-override-${o.date}`}><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          {overrides.length === 0 && <p className="py-6 text-center text-sm text-[#8C7E70]">No date overrides set.</p>}
        </div>
      </Panel>
    </div>
  );
}

function InboxPanel() {
  const [sub, setSub] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [messages, setMessages] = useState([]);
  const load = () => {
    adminInboxBookings().then(setBookings).catch(() => {});
    adminInboxMessages().then(setMessages).catch(() => {});
  };
  useEffect(() => { load(); }, []);
  const fmt = (s) => { try { return new Date(s).toLocaleString(); } catch { return s; } };
  const toggleB = async (b) => { await adminReadBooking(b.reference, !b.read); load(); };
  const delB = async (ref) => { if (!window.confirm("Delete this booking request?")) return; await adminDeleteBooking(ref); load(); };
  const toggleM = async (m) => { await adminReadMessage(m.id, !m.read); load(); };
  const delM = async (id) => { if (!window.confirm("Delete this message?")) return; await adminDeleteMessage(id); load(); };
  const unreadB = bookings.filter((b) => !b.read).length;
  const unreadM = messages.filter((m) => !m.read).length;

  const Row = ({ read, children, onToggle, onDelete, testid }) => (
    <div className={`rounded-sm border p-4 ${read ? "border-[#D4AF37]/20 bg-white/40" : "border-[#B89759]/60 bg-[#F4EFE6]/70"}`} data-testid={testid}>
      {children}
      <div className="mt-3 flex items-center gap-3 border-t border-[#D4AF37]/20 pt-3">
        <button className="flex items-center gap-1.5 text-xs text-[#4A423A] hover:text-[#1C1815]" onClick={onToggle} data-testid={`${testid}-toggle-read`}>
          {read ? <Circle className="h-3.5 w-3.5" /> : <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />} Mark as {read ? "unread" : "read"}
        </button>
        <button className="flex items-center gap-1.5 text-xs text-red-600 hover:text-red-700" onClick={onDelete} data-testid={`${testid}-delete`}><Trash2 className="h-3.5 w-3.5" /> Delete</button>
      </div>
    </div>
  );

  return (
    <Panel>
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setSub("bookings")} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs uppercase tracking-[0.12em] ${sub === "bookings" ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]" : "border-[#D4AF37]/40 text-[#4A423A]"}`} data-testid="inbox-tab-bookings">
          <CalendarDays className="h-4 w-4" /> Bookings {unreadB > 0 && <span className={`rounded-full px-2 text-[10px] ${sub === "bookings" ? "bg-[#FAF7F2]/25 text-[#FAF7F2]" : "bg-[#D4AF37]/30 text-[#9A7A3F]"}`}>{unreadB}</span>}
        </button>
        <button onClick={() => setSub("messages")} className={`flex items-center gap-2 rounded-full border px-4 py-2 text-xs uppercase tracking-[0.12em] ${sub === "messages" ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]" : "border-[#D4AF37]/40 text-[#4A423A]"}`} data-testid="inbox-tab-messages">
          <Mail className="h-4 w-4" /> Messages {unreadM > 0 && <span className={`rounded-full px-2 text-[10px] ${sub === "messages" ? "bg-[#FAF7F2]/25 text-[#FAF7F2]" : "bg-[#D4AF37]/30 text-[#9A7A3F]"}`}>{unreadM}</span>}
        </button>
      </div>

      <div className="mt-6 space-y-3">
        {sub === "bookings" && bookings.map((b) => (
          <Row key={b.reference} read={b.read} testid={`inbox-booking-${b.reference}`}
            onToggle={() => toggleB(b)} onDelete={() => delB(b.reference)}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-serif text-lg text-[#1C1815]">{b.name} <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#9A7A3F]">{b.reference}</span></span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C7E70]">{fmt(b.created_at)}</span>
            </div>
            <p className="mt-1 text-sm text-[#3D352E]">{b.service} · {b.date} at {b.time}</p>
            <p className="mt-1 text-xs text-[#8C7E70]">{b.email} · {b.phone}</p>
            {b.message && <p className="mt-2 text-sm font-light italic text-[#4A423A]">"{b.message}"</p>}
          </Row>
        ))}
        {sub === "bookings" && bookings.length === 0 && <p className="py-8 text-center text-sm text-[#8C7E70]">No booking requests yet.</p>}

        {sub === "messages" && messages.map((m) => (
          <Row key={m.id} read={m.read} testid={`inbox-message-${m.id}`}
            onToggle={() => toggleM(m)} onDelete={() => delM(m.id)}>
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-serif text-lg text-[#1C1815]">{m.name}</span>
              <span className="font-mono text-[10px] uppercase tracking-[0.15em] text-[#8C7E70]">{fmt(m.created_at)}</span>
            </div>
            <p className="mt-1 text-xs text-[#8C7E70]">{m.email}{m.phone ? ` · ${m.phone}` : ""}</p>
            <p className="mt-2 text-sm font-light text-[#4A423A]">{m.message}</p>
          </Row>
        ))}
        {sub === "messages" && messages.length === 0 && <p className="py-8 text-center text-sm text-[#8C7E70]">No messages yet.</p>}
      </div>
    </Panel>
  );
}

export default function AdminDashboard() {
  const { admin, checked, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("Artworks");

  useEffect(() => {
    if (checked && !admin) navigate("/admin/login", { replace: true });
  }, [checked, admin, navigate]);

  if (!checked || !admin) {
    return <div className="flex min-h-screen items-center justify-center bg-[#FAF7F2]"><Loader2 className="h-8 w-8 animate-spin text-[#B89759]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FAF7F2]" data-testid="admin-dashboard">
      <header className="sticky top-0 z-40 border-b border-[#D4AF37]/25 bg-[#FAF7F2]/95 backdrop-blur">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-4 sm:px-6">
          <div className="flex items-center gap-3">
            <img src="/assets/logo.png" alt="logo" className="h-9 w-9 rounded-full object-cover ring-1 ring-[#D4AF37]/50" />
            <span className="font-display text-xs font-semibold tracking-[0.2em] text-[#1C1815]">STUDIO ADMIN</span>
          </div>
          <button className="btn-ghost-light !border-[#1C1815]/20 !text-[#4A423a]" onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout-button">
            <LogOut className="h-4 w-4" /> Sign Out
          </button>
        </div>
      </header>
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
        <div className="mb-8 flex flex-wrap gap-2 border-b border-[#D4AF37]/25 pb-4">
          {TABS.map((tb) => (
            <button key={tb} onClick={() => setTab(tb)}
              className={`rounded-full border px-5 py-2 text-xs uppercase tracking-[0.14em] transition-colors ${tab === tb ? "border-[#1C1815] bg-[#1C1815] text-[#FAF7F2]" : "border-[#D4AF37]/40 text-[#4A423A] hover:border-[#B89759]"}`}
              data-testid={`admin-tab-${tb.toLowerCase().replace(/\s+/g, "-")}`}>
              {tb}
            </button>
          ))}
        </div>
        {tab === "Artworks" && <ArtworksPanel />}
        {tab === "Availability" && <AvailabilityPanel />}
        {tab === "Inbox" && <InboxPanel />}
        {tab === "Site Text" && <SiteTextPanel />}
        {tab === "Pricing" && <PricingPanel />}
        {tab === "Testimonials" && <TestimonialsPanel />}
      </div>
    </div>
  );
}
